import dayjs from 'dayjs';
import questionKinds from '@/services/constants/questionKinds';
import { alertController, toastController } from '@ionic/vue';

const MACHINE_FORMAT = 'YYYY-MM-DD[T]HH:mm';

const mixinReportCommon = {
  methods: {
    /**
     * On date click, set current day and init report for that day (if needed)
     */
    onDateClick(newDate, hook = 'mission_reports') {
      this.currentDay = newDate;

      const currentReport = this.reports[this.currentDay];
      let reportForDay = currentReport
        ? JSON.parse(JSON.stringify(currentReport))
        : {};

      if (!reportForDay.custom_questions) {
        reportForDay.custom_questions = {};
      }

      // if time report, init hours details
      if (
        hook === 'time_reports' &&
        (!reportForDay.hours_details ||
          !reportForDay.hours_details.agents ||
          !reportForDay.hours_details.agents.length)
      ) {
        reportForDay.hours_details = { agents: [] };
      }

      // if time report, init timer details
      if (
        hook === 'time_reports' &&
        (!reportForDay.timer_hours_details ||
          !reportForDay.timer_hours_details.agents ||
          !reportForDay.timer_hours_details.agents.length)
      ) {
        reportForDay.timer_hours_details = { agents: [] };
      }

      reportForDay = this.onDateClickCommon(reportForDay);
      this.reports[this.currentDay] = reportForDay;
    },

    onDateClickCommon(reportForDay, machineId = null) {
      // get all fields in one big array
      const questions = machineId ? this.questions[machineId] : this.questions;
      const allFields = Object.values(questions).flatMap(question =>
        Object.values(question.fields),
      );

      // Init all answers to empty values when revelant,
      // or format them in some cases
      allFields.forEach(field => {
        const empty = this.getEmptyForField(field);

        if (!field.property) {
          if (field.uniqid in reportForDay.custom_questions) {
            return;
          }

          reportForDay.custom_questions[field.uniqid] = empty;
          return;
        }

        // When timestamp exists, format to machine format to avoid Ionic Picker not working
        if (reportForDay[field.property + '_timestamp']) {
          const tsKey = field.property + '_timestamp';
          const tsValue = reportForDay[tsKey];
          if (tsValue) {
            reportForDay[tsKey] = dayjs(tsValue).format(MACHINE_FORMAT);
          }
        }

        if (
          reportForDay[field.property] !== undefined &&
          reportForDay[field.property] !== null &&
          reportForDay[field.property] !== ''
        )
          return;

        reportForDay[field.property] = empty;
      });

      return reportForDay;
    },

    getEmptyForField(field) {
      if (field.kind === questionKinds.DATERANGE) return [null, null];
      if (field.kind === questionKinds.FILE) return [];
      return null;
    },

    /**
     * Get value for field
     *
     * if not custom question, we get the value from the reports
     * else, we get the value from the custom_questions
     */
    getValueForBooleanField(field, removeNullOption = false, machine = null) {
      const report = machine
        ? this.reports[machine][this.currentDay]
        : this.reports[this.currentDay];

      const value = field.property
        ? report[field.property]
        : report.custom_questions[field.uniqid];

      if (value === true || value === 'true') return 'true';
      if (removeNullOption || value === false || value === 'false')
        return 'false';

      return null;
    },

    /**
     * BOOLEAN is a possible multiple type
     */
    onBooleanChange(newVal, field, machine = null) {
      let casted = null;
      if (newVal === 'true') casted = true;
      if (newVal === 'false') casted = false;

      if (field.property && machine) {
        this.reports[machine][this.currentDay][field.property] = casted;
        return;
      }

      if (field.property) {
        this.reports[this.currentDay][field.property] = casted;
        return;
      }

      if (machine) {
        this.reports[machine][this.currentDay].custom_questions[field.uniqid] =
          casted;
        return;
      }

      this.reports[this.currentDay].custom_questions[field.uniqid] = casted;
    },

    getMissionRelatedData() {
      if (this.couponId) {
        return {
          related_id: this.couponId,
          related_type: 'App\\RentalCoupon',
        };
      }

      const { params, fullPath } = this.$route;
      const isReservation = fullPath.includes('chantier');

      return {
        related_id: isReservation ? params.id : params.idItem,
        related_type: isReservation ? 'App\\Reservation' : 'App\\TransportItem',
      };
    },

    getWaitingFilesFormData() {
      const formData = new FormData();

      // add all waiting files to our formData
      Object.values(this.waitingFiles).forEach(waitingFile => {
        let key = `${waitingFile.kind}|${waitingFile.randomId}|files`;
        formData.append(
          `${key}[]`,
          waitingFile,
          waitingFile.newName || waitingFile.name,
        );
        formData.append('keys[]', key);
      });

      return formData;
    },

    getAllDataToSend(hook = 'mission_reports') {
      const formData = this.getWaitingFilesFormData();
      const dataToSend = this.getMissionRelatedData();
      // make data sent full compatible with "old" time reports backend
      const reportsKey = hook === 'mission_reports' ? 'reports' : 'entries';

      dataToSend[reportsKey] = this.getDataToSend(hook, this.reports);
      dataToSend.attachmentsToUpdate = this.attachmentsToUpdate;
      dataToSend.attachmentsToDelete = this.attachmentsToDelete;
      formData.append('report_data', JSON.stringify(dataToSend));

      return formData;
    },

    getDataToSend(hook, reports, machineId = null) {
      const questions = machineId ? this.questions[machineId] : this.questions;
      const flatten = Object.values(questions).flatMap(question =>
        Object.values(question.fields),
      );

      const output = [];

      Object.entries(reports).forEach(([day, value]) => {
        // make data sent full compatible with "old" time reports backend
        const line = hook === 'mission_reports' ? { day } : { date: day };

        flatten.forEach(field => {
          // since daterange are stored as string in database we need to format them
          if (field.kind === this.questionKinds.DATERANGE) {
            if (!value[field.property].filter(v => !!v).length) {
              line[field.property] = null;
              return;
            }

            const formatted = value[field.property].map(v =>
              v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '',
            );

            line[field.property] = `[${formatted.join(',')}]`;
          } else if (
            field.property &&
            field.kind === this.questionKinds.BOOLEAN
          ) {
            line[field.property] =
              value[field.property] === true || value[field.property] === 'true'
                ? true
                : false;

            return;
          } else if (field.property) {
            line[field.property] = value[field.property] || null;

            // if we have a timestamp number, we need to add the timestamp
            if (field.kind === questionKinds.TIMESTAMP_NUMBER) {
              line[field.property + '_timestamp'] =
                value[field.property + '_timestamp'] || null;
            }

            return;
          }

          /**
           * If not custom question, don't go further
           * Ex:
           * - We have a report created for the monday but without any custom question (custom_questions is NULL)
           * - Admin have created custom question monday, after the report was created
           * - User re-open the week, and try to create a report for thursday
           * - User, save a first time, The re-save right after
           */
          if (
            !value.custom_questions ||
            !(field.uniqid in value.custom_questions)
          )
            return;

          line.custom_questions = line.custom_questions || {};
          line.custom_questions[field.uniqid] =
            value.custom_questions[field.uniqid];
        });

        if (value.hours_details) {
          line.hours_details = value.hours_details;
        }

        if (value.timer_hours_details) {
          line.timer_hours_details = value.timer_hours_details;
        }

        if (value.hours_agent) {
          line.hours_agent = value.hours_agent;
        }

        if (value.hours_night_agent) {
          line.hours_night_agent = value.hours_night_agent;
        }

        output.push(line);
      });

      return output;
    },

    async sendToast(message, color = 'success') {
      const toast = await toastController.create({
        message,
        duration: 3000,
        color,
        position: 'top',
      });
      await toast.present();
    },

    /**
     * Get completion status of given report
     * - 'empty' if no fields are filled
     * - 'partial' if some fields are filled
     * - 'full' if all fields are filled
     * @param report
     */
    getCompletionStatuses(report, machineId = null) {
      // if no report
      if (!report) return 'empty';

      const questions = machineId ? this.questions[machineId] : this.questions;

      // Get all fields from all questions in a flat array
      const allFields = Object.values(questions).flatMap(question =>
        Object.values(question.fields),
      );

      if (!allFields.length) return 'empty';

      // Use Array.prototype.filter to count filled fields
      const filledCount = allFields.filter(field => {
        let value = null;
        if (
          field.uniqid &&
          report.custom_questions &&
          field.uniqid in report.custom_questions
        ) {
          value = report.custom_questions[field.uniqid];
        } else if (field.property && report[field.property]) {
          value = report[field.property];
        }

        return this.isFieldFilled(field, value, report);
      }).length;

      if (filledCount === 0) return 'empty';
      if (filledCount === allFields.length) return 'full';
      return 'partial';
    },

    /**
     * Check if a field is filled based on its type
     * - DATERANGE is filled if at least one of the values is present
     * - FILE is filled if there are attachments
     * - TIMESTAMP_NUMBER is filled if both value and timestamp are present
     * - BOOLEAN is filled if value is not null or undefined (empty string counts as filled for boolean toggles)
     * - For "simple" types (TEXTAREA, DATE, NUMBER etc..) is filled if value is not null, undefined or empty string
     */
    isFieldFilled(field, value, data) {
      // For different field types, determine if they're filled
      if (field.kind === this.questionKinds.DATERANGE) {
        // DateRange is filled if at least one of the values is present
        return Array.isArray(value) && value.some(Boolean);
      }

      if (field.kind === this.questionKinds.FILE) {
        // Check if there are attachments
        if (!field.property) {
          return (
            Array.isArray(data.custom_questions?.[field.uniqid]) &&
            data.custom_questions[field.uniqid].length > 0
          );
        }

        return Array.isArray(value) && value.length > 0;
      }

      if (field.kind === this.questionKinds.TIMESTAMP_NUMBER) {
        // Check both the value and its timestamp
        const timestampKey = field.property + '_timestamp';
        return Boolean(value) && Boolean(data[timestampKey]);
      }

      if (field.kind === this.questionKinds.BOOLEAN) {
        // Check for boolean values (including empty string which counts as filled for boolean toggles)
        return value !== null && value !== undefined;
      }

      // For "simple" types (TEXTAREA, DATE, NUMBER)
      return value !== null && value !== undefined && value !== '';
    },

    async confirm(daysToSend = [], origin = 'time-reports') {
      // no days ? no confirmation needed
      if (!daysToSend || !daysToSend.length) {
        return null;
      }

      // if machine reports, we need to go a little further inside reports to get days
      const daysObject =
        origin === 'time-reports'
          ? this.reports
          : this.reports[Object.keys(this.reports)[0]];

      const filteredDays = daysToSend.filter(day => {
        return daysObject[day] && daysObject[day].status !== 'sent';
      });

      // if all days are already sent, no need to show confirmation
      if (!filteredDays.length) {
        this.sendToast(
          'Aucun jour à envoyer. Tous les jours sont déjà envoyés.',
          'warning',
        );

        return null;
      }

      const alertConfig = {
        header: 'Journées à envoyer',
        message:
          'Choisissez les journées que vous souhaitez envoyer au bureau.',
        buttons: [
          {
            text: 'Annuler',
            role: 'cancel',
          },
          {
            text: 'Confirmer',
            role: 'confirm',
          },
        ],
      };

      // If only one day, show simple confirmation without checkboxes
      if (filteredDays.length === 1) {
        const formattedDate = dayjs(filteredDays[0]).format('dddd D MMMM');
        alertConfig.header = 'Confirmation';
        alertConfig.message = `Envoyer le rapport du ${formattedDate}\u00A0?`;
      } else {
        // when multiple days, create input for each days
        const inputs = filteredDays.map(day => ({
          label: dayjs(day).format('dddd D MMMM'),
          type: 'checkbox',
          value: day,
          checked: true,
        }));

        alertConfig.inputs = inputs;
      }

      const popUp = await alertController.create(alertConfig);
      await popUp.present();
      const { role, data } = await popUp.onDidDismiss();

      // If cancelled, return null
      if (role !== 'confirm') return null;

      // if only one day, return it
      if (filteredDays.length === 1) {
        return filteredDays;
      }

      // Return selected days, or empty array if none selected
      return data.values || [];
    },
  },
};

export { mixinReportCommon };
